import type { Commentary, CommentaryV2 } from '@aloft/toolkit-shared';
import { isDefined } from '@aloft/toolkit-shared';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { mergeBasementAreaDisclosureNote } from '../../utils/basementAreaDisclosure.ts';
import type { CommentaryState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { submitReconciliation, submitReconciliationDataSheet } from './reconciliationSlice.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export const initialCommentaryState: CommentaryState = {
  marketConditionsV2: {
    filter: {
      type: 'Zip Code',
    },
  },
};

const commentarySlice = createSlice({
  name: 'commentary',
  initialState: initialCommentaryState,
  reducers: {
    setCommentary: (state, action: PayloadAction<Commentary>) => {
      Object.assign(state, action.payload);
    },
    setCommentaryV2: (state, action: PayloadAction<CommentaryV2>) => {
      Object.assign(state, {
        ...action.payload,
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    commentaryMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (state.syncState) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
    /**
     * Merges the "Basement Area Disclosure" modal's note into the Reconciliation
     * Commentary field(s), so it's visible on the Reconciliation page and included in the PDF.
     */
    setBasementAreaDisclosureCommentary: (
      state,
      action: PayloadAction<{ note: string; hasArvOption: boolean }>,
    ) => {
      const { note, hasArvOption } = action.payload;
      const reconciliation = state.reconciliation ?? {};
      const updates: Partial<NonNullable<typeof state.reconciliation>> = {};

      if (hasArvOption) {
        const mergedAiv = mergeBasementAreaDisclosureNote(
          reconciliation.aivReconciliationCommentary ?? reconciliation.reconciliationCommentary,
          note,
        );
        updates.aivReconciliationCommentary = mergedAiv;
        updates.arvReconciliationCommentary = mergeBasementAreaDisclosureNote(
          reconciliation.arvReconciliationCommentary,
          note,
        );
        updates.reconciliationCommentary = mergedAiv;
      } else {
        updates.reconciliationCommentary = mergeBasementAreaDisclosureNote(
          reconciliation.reconciliationCommentary,
          note,
        );
      }

      Object.assign(state, {
        syncState: getBumpedSyncState(state.syncState),
        reconciliation: {
          ...state.reconciliation,
          ...updates,
        },
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialCommentaryState);
    builder.addCase(submitReconciliationDataSheet, (state, action) => {
      const { reconciliationCommentary, aivReconciliationCommentary, arvReconciliationCommentary } =
        action.payload;
      const hasCommentaryUpdate =
        isDefined(reconciliationCommentary) ||
        isDefined(aivReconciliationCommentary) ||
        isDefined(arvReconciliationCommentary);
      if (hasCommentaryUpdate) {
        const reconciliationUpdates: Partial<NonNullable<(typeof state)['reconciliation']>> = {};
        if (isDefined(aivReconciliationCommentary)) {
          reconciliationUpdates.aivReconciliationCommentary = aivReconciliationCommentary;
          reconciliationUpdates.reconciliationCommentary = aivReconciliationCommentary;
        } else if (isDefined(reconciliationCommentary)) {
          reconciliationUpdates.reconciliationCommentary = reconciliationCommentary;
        }
        if (isDefined(arvReconciliationCommentary)) {
          reconciliationUpdates.arvReconciliationCommentary = arvReconciliationCommentary;
        }
        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            ...reconciliationUpdates,
          },
        });
      }
    });
    builder.addCase(submitReconciliation, (state, action) => {
      const {
        totalCostToCureCommentary,
        reconciliationCommentary,
        aivReconciliationCommentary,
        arvReconciliationCommentary,
        highestAndBestUseCommentary,
        revisionCommentary,
        alternativeComparablesCommentary,
        feasibilityCommentary,
        aivDiscrepancyCommentary,
        arvDiscrepancyCommentary,
        arvBelowAivOverrideCommentary,
      } = action.payload;

      const hasSplitCommentary =
        isDefined(aivReconciliationCommentary) && isDefined(arvReconciliationCommentary);
      const hasSingleCommentary = isDefined(reconciliationCommentary) && !hasSplitCommentary;
      const shouldUpdateMainFields =
        (hasSplitCommentary || hasSingleCommentary) && isDefined(highestAndBestUseCommentary);
      const shouldUpdateFeasibility = 'feasibilityCommentary' in action.payload;

      if (shouldUpdateMainFields || shouldUpdateFeasibility) {
        const updates: Partial<typeof state.reconciliation> = {};

        if (shouldUpdateMainFields) {
          updates.totalCostToCureCommentary = totalCostToCureCommentary;
          updates.highestAndBestUseCommentary = highestAndBestUseCommentary;
          updates.revisionCommentary = revisionCommentary;
          updates.alternativeComparablesCommentary = alternativeComparablesCommentary;
          updates.aivDiscrepancyCommentary = aivDiscrepancyCommentary;
          updates.arvDiscrepancyCommentary = arvDiscrepancyCommentary;
          updates.arvBelowAivOverrideCommentary = arvBelowAivOverrideCommentary;
          if (hasSplitCommentary) {
            updates.aivReconciliationCommentary = aivReconciliationCommentary;
            updates.arvReconciliationCommentary = arvReconciliationCommentary;
            updates.reconciliationCommentary = aivReconciliationCommentary;
          } else {
            updates.reconciliationCommentary = reconciliationCommentary;
          }
        }

        if (shouldUpdateFeasibility) {
          updates.feasibilityCommentary = feasibilityCommentary ?? '';
        }

        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            ...updates,
          },
        });
      }
    });
  },
});

export const {
  setCommentary,
  setCommentaryV2,
  commentaryMutationComplete,
  setBasementAreaDisclosureCommentary,
} = commentarySlice.actions;

export const commentaryReducer = commentarySlice.reducer;
